import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton, Divider } from '@mui/material';
import Box from '@mui/material/Box';
import { useState, useEffect } from 'react';

import Drawer from '@/shared/components/core-ui/drawer/drawer.component';
import { useLocalization } from '@/shared/hooks/useLocalization';

import SideMenuContent from './components/side-menu-content';

interface SideMenuProps {
  mobileOpen: boolean;
  setMobileOpen: (mobileOpen: boolean) => void;
}

const MENU_COLLAPSE_KEY = 'sideMenuCollapsed';

const SideMenu = ({ mobileOpen, setMobileOpen }: SideMenuProps) => {
  const { isRTLDirect: isRTL } = useLocalization();
  
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const savedState = localStorage.getItem(MENU_COLLAPSE_KEY);
    return savedState ? JSON.parse(savedState) : false;
  });

  const expandedWidth = 256;
  const collapsedWidth = 64;
  const drawerWidth = isCollapsed ? collapsedWidth : expandedWidth;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCollapseToggle = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem(MENU_COLLAPSE_KEY, JSON.stringify(newState));
  };

  // Sync collapse state with localStorage when component mounts
  useEffect(() => {
    const savedState = localStorage.getItem(MENU_COLLAPSE_KEY);
    if (savedState !== null) {
      setIsCollapsed(JSON.parse(savedState));
    }
  }, []);

  return (
    <Box
      component="nav"
      sx={{
        width: { xs: 0, md: drawerWidth },
        flexShrink: { xs: 0 },
        transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms'
      }}
      aria-label="SideMenu"
    >
      <Drawer
        config={{ width: drawerWidth, anchor: isRTL ? 'right' : 'left', isOpen: mobileOpen, appearOverAppbar: false }}
        eventHandlers={{ handleClose: handleDrawerToggle }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <SideMenuContent isCollapsed={isCollapsed} />
          <Box sx={{ marginTop: 'auto' }}>
            <Divider />
            <Box sx={{ p: 1, display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
              <IconButton onClick={handleCollapseToggle}>
                {isCollapsed ? (isRTL ? <ChevronLeftIcon /> : <ChevronRightIcon />) : (isRTL ? <ChevronRightIcon /> : <ChevronLeftIcon />)}
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Drawer>
      <Drawer
        config={{
          width: drawerWidth,
          variant: 'permanent',
          anchor: isRTL ? 'right' : 'left',
          appearOverAppbar: false
        }}
        styles={{
          display: { xs: 'none', md: 'flex' },
          '& .MuiDrawer-paper': {
            transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
            overflowX: 'hidden',
            width: drawerWidth
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <SideMenuContent isCollapsed={isCollapsed} />
          <Box sx={{ marginTop: 'auto' }}>
            <Divider />
            <Box
              sx={{ p: 1, display: 'flex', justifyContent: isCollapsed ? 'center' : (isRTL ? 'flex-start' : 'flex-end') }}
            >
              <IconButton
                onClick={handleCollapseToggle}
                sx={{
                  bgcolor: 'background.paper',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                {isCollapsed ? (
                  isRTL ? <ChevronLeftIcon color="primary" /> : <ChevronRightIcon color="primary" />
                ) : (
                  isRTL ? <ChevronRightIcon color="primary" /> : <ChevronLeftIcon color="primary" />
                )}
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default SideMenu;
